/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  ContactDisplay,
  ContactDuplicateSearchMode,
  ContactRoleType,
  ContactType,
} from '@gh/builder-shared-data';
import { ContextRoute, getContextRouteInitData } from '@gh/common-internal-shared';
import { EnumDictionary, TextDictionary } from '@gh/common-shared';
import { AutocompleteLoadMoreEvent, DialogService } from '@gh/core-ui';
import { assign } from 'lodash';
import { Observable } from 'rxjs';
import { getContactName, getContactPhones, getFullContactAddress } from '../../contact-details-util';
import { ContactDetailsBusinessService } from '../../resources/contact-details-business.service';
import { ContactDuplicateFilter, ContactDuplicateItem } from '../../resources/model/contact-details-common.model';
import { ContactLocation } from '../../resources/model/contact-details.model';
import { ContactDetailsModel } from '../../services/contact-details-model.service';
import { ContactDetailsCreditorCommonFormState } from '../contact-details-creditor-common-tab/contact-details-creditor-common-form.service';
import { ContactDetailsFormState } from '../contact-details-tab-container/contact-details-form.service';
import { ContactDetailsTabContainerComponent } from '../contact-details-tab-container/contact-details-tab-container.component';
import { contactDuplicatesDialog } from '../contact-duplicates-dialog/contact-duplicates-dialog.route';
import { ContactDetailsCommonFormState } from './contact-details-common-form.service';

@Component({
  selector: 'gh-contact-details-common-tab',
  template: require('./contact-details-common-tab.component.html'),
  styles: [require('./contact-details-common-tab.component.scss')],
})
export class ContactDetailsCommonTabComponent implements OnInit {
  contactTypes: EnumDictionary<ContactType>;
  contactRoleTypes: EnumDictionary<ContactRoleType>;
  contactDisplay: EnumDictionary<ContactDisplay>;
  states: TextDictionary;

  constructor(private route: ContextRoute,
              private model: ContactDetailsModel,
              private userContext: BuilderUserContext,
              private contactBusiness: ContactDetailsBusinessService,
              public masterPage: ContactDetailsTabContainerComponent,
              public form: ContactDetailsCommonFormState,
              public rootForm: ContactDetailsFormState,
              private dialogService: DialogService) {
  }

  get officeView(): boolean {
    return this.userContext.officeView;
  }

  get emailRequired(): boolean {
    return this.form.isEmailRequired();
  }

  get businessNameHidden(): boolean {
    return this.form.group.value.type !== 'Business';
  }

  get individualNameHidden(): boolean {
    return this.form.group.value.type !== 'Individual' && !this.form.isEmployeeAssigned;
  }

  ngOnInit(): void {
    assign(this, getContextRouteInitData(this.route));
  }

  onRoleChange(type: ContactRoleType): void {
    this.form.setRoleType(type);
  }

  onNameUpdate(): void {
    const title = getContactName(this.form.value);

    this.masterPage.setTitle(title);
    this.form.setAddressee(title);
  }

  onEmailUpdate(): void {
    const creditorForm = this.rootForm.getChildForm('creditorSupplier');

    if (creditorForm) {
      (<ContactDetailsCreditorCommonFormState>creditorForm.getChildForm('common')).updateEmail();
    }
  }

  onTypeAssign(type: ContactType): void {
    this.masterPage.assignType(type);
    if (type === ContactType.Employee) {
      this.form.value.type && this.form.setRoleType(ContactRoleType.Individual);
      return;
    }
    this.form.value.type && this.form.setRoleType(this.form.value.type);
  }

  onAssignedTypeClick(type: ContactType): void {
    const initTypeValue = this.model.original.common.contactTypes.find((contactType) => contactType.type === ContactType[type]);
    if (this.model.creationMode || (!!initTypeValue && !initTypeValue.assigned)) {
      this.masterPage.unAssignType(type);
    }
  }

  showDuplicates(filter: ContactDuplicateFilter): Observable<any> {
    const common = this.form.value;
    const original: ContactDuplicateItem = {
      name: getContactName(common),
      phones: getContactPhones(common),
      address: getFullContactAddress(common),
    };

    return this.dialogService.openWithInput(contactDuplicatesDialog, {
      duplicateFilter: filter,
      original,
    }).afterClosed();
  }

  showBusinessDuplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.BusinessName,
      businessName: this.form.value.businessName,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipBusinessDuplicatesCheck'));
  }

  showIndividualDuplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.IndividualName,
      firstName: this.form.value.individualName && this.form.value.individualName.firstName,
      lastName: this.form.value.individualName && this.form.value.individualName.lastName,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipIndividualDuplicatesCheck'));
  }

  showPhone1Duplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.Phone1,
      phone1: this.form.value.phone1,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipPhone1DuplicatesCheck'));
  }

  showPhone2Duplicates(): void {
    this.showDuplicates({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.Phone2,
      phone2: this.form.value.phone2,
    }).subscribe(() => this.form.confirmations.markAsApproved('skipPhone2DuplicatesCheck'));
  }

  loadLocations(event: AutocompleteLoadMoreEvent): void {
    event.load(this.contactBusiness.findLocations(event.query));
  }

  onSelectLocation(location: ContactLocation): void {
    this.form.setLocation(location);
  }
}
